/**
 * DealModalHeader - Header compacto do modal premium
 * Layout horizontal com status, código N.XXXXXXXX, título, valor e ações
 *
 * Versão: 1.10.0
 * RELEASE: 2026-10-17 v1.10.0 - Ação opcional "Criar O.S." (serviços do negócio viram itens da O.S.).
 * RELEASE: 2026-09-12 v1.9.0 - Nova ação "Alterar fase" (ícone GitBranch),
 *          padronizada com o modal de Tarefas. Fica visível também quando o
 *          negócio está ganho/perdido, permitindo mover para outra
 *          fase/pipeline/workspace (ex.: pós-venda/onboarding), desde que o
 *          usuário tenha permissão de mudança de fase.
 * Versão anterior: 1.6.0
 * RELEASE: 2026-06-01 v1.6.0 - Botão "Iniciar playbook nesta fase" promovido
 *          para botão dedicado (Sparkles) ao lado de Editar no desktop.
 *          Sempre visível quando o deal está aberto e há eligibility carregada;
 *          fica disabled com tooltip explicando o motivo (no_playbook, no_steps,
 *          cadence_active). No mobile vira item dinâmico no DropdownMenu.
 * RELEASE: 2026-06-01 v1.5.0 - Item inicial no kebab (mobile + desktop).
 * RELEASE: 2026-05-27 v1.4.0 - Permissões granulares por fase.
 * RELEASE: 2026-02-14 v1.3.0 - Ícone de agendamento movido para QuickActivityBar.
 */

import React from 'react';
import {
  Pencil,
  Trophy,
  XCircle,
  RotateCcw,
  Trash2,
  X,
  MoreVertical,
  Zap,
  Wrench,
  // v1.9.0: ícone da ação "Alterar fase" (mesmo usado no modal de Tarefas)
  GitBranch

} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CrmDeal } from '@/hooks/useCrmDeals';
import { DealStatusBadgePremium } from './components/DealStatusBadgePremium';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDealCode } from '@/lib/formatters';
import { useIsMobile } from '@/hooks/use-mobile';
import { WorkflowStageChip } from '@/components/workflow/WorkflowStageChip';
// v1.8.0: trava do Workflow explicada antes do clique no botão Ganho
import { WorkflowGuardGate } from '@/components/workflow/WorkflowGuardGate';
import type { WorkflowGuardDecision } from '@/hooks/useWorkflowGuardCheck';

import {
  PLAYBOOK_REASON_LABELS,
  type DealPlaybookEligibility,
} from '@/hooks/useDealPlaybookEligibility';

// Props do componente
interface DealModalHeaderProps {
  deal: CrmDeal;
  canMarkAsWon?: boolean; // v2.0.0: Indica se pode marcar como ganho (tem produtos/serviços)
  // v1.4.0: Permissões granulares por fase. Default `true` preserva legacy.
  canEditDeal?: boolean;
  canChangePhase?: boolean;
  canReopenDeal?: boolean;
  canDeleteDeal?: boolean;
  onEdit: () => void;
  onMarkWon: () => void;
  onMarkLost: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onClose: () => void;
  /**
   * v1.9.0: abre o modal "Alterar fase" do negócio. Disponível também para
   * negócios ganhos/perdidos (o servidor continua validando as regras).
   */
  onChangePhase?: () => void;
  // v1.6.0: Acionamento manual do playbook. O botão é renderizado sempre que
  // o deal está aberto; eligibility define se fica habilitado e o tooltip.
  onStartPlaybook?: () => void;
  startPlaybookLabel?: string;
  // v1.6.0: Eligibility já com `reason` derivado. null = ainda carregando.
  playbookEligibility?: DealPlaybookEligibility | null;
  /**
   * v1.8.0: decisão antecipada do Workflow para a ação `crm.deal.win`.
   * Quando `bloqueado`, o Ganho fica desabilitado com tooltip do motivo.
   * A decisão final continua no banco (gatilho); isto é só feedback de UI.
   */
  workflowGuardDecision?: WorkflowGuardDecision | null;
  /** v1.10.0: cria O.S. com cliente/negócio preenchidos (omitir para ocultar) */
  onCreateServiceOrder?: () => void;
}

/**
 * Header compacto do modal (48px)
 */
export function DealModalHeader({
  deal,
  canMarkAsWon = true,
  canEditDeal = true,
  canChangePhase = true,
  canReopenDeal = true,
  canDeleteDeal = true,
  onEdit,
  onMarkWon,
  onMarkLost,
  onReopen,
  onDelete,
  onClose,
  onChangePhase,
  onStartPlaybook,
  startPlaybookLabel = 'Iniciar playbook nesta fase',
  playbookEligibility = null,
  workflowGuardDecision = null,
  onCreateServiceOrder,
}: DealModalHeaderProps) {
  const isMobile = useIsMobile();
  // Verificar se o negócio está fechado (ganho ou perdido)
  const isDealClosed = deal.status === 'won' || deal.status === 'lost';

  // v1.4.0: Visibilidade de cada ação combinando status do negócio + permissão granular
  const showEdit = deal.status === 'open' && canEditDeal;
  const showMarkWonLost = deal.status === 'open' && canChangePhase;
  const showReopen = deal.status !== 'open' && canReopenDeal;
  const showDelete = canDeleteDeal;
  // v1.9.0: "Alterar fase" independe do status (ganho/perdido também pode mover
  // para outra fase/pipeline/workspace); depende apenas da permissão granular.
  const showChangePhase = !!onChangePhase && canChangePhase;
  // v1.7.0: Botão de playbook só aparece quando o deal é ELEGÍVEL (workspace
  // com playbook_mode, playbook per_phase ativo no pipeline, steps na fase
  // atual e sem cadência ativa). Caso contrário fica oculto — evita poluir o
  // header para a maioria dos usuários que não opera playbook de vendas.
  const playbookEligible = playbookEligibility?.is_eligible === true;
  const showStartPlaybook =
    deal.status === 'open' && !!onStartPlaybook && playbookEligible;
  const playbookTooltip = PLAYBOOK_REASON_LABELS.eligible;
  // v1.8.0: Ganho combina regra de produtos/serviços + trava do Workflow.
  // O motivo da trava tem prioridade no tooltip (vem configurado no processo).
  const ganhoBloqueadoPeloWorkflow = workflowGuardDecision?.bloqueado === true;
  const ganhoLiberado = canMarkAsWon && !ganhoBloqueadoPeloWorkflow;
  const motivoGanhoBloqueado = ganhoBloqueadoPeloWorkflow
    ? (workflowGuardDecision?.mensagem ?? 'Existe uma pendência do processo neste negócio.')
    : 'Adicione ao menos um produto/serviço';
  // Indica se existe ao menos uma ação além do Fechar (X) — controla separador desktop
  const hasAnyAction =
    showEdit || showMarkWonLost || showReopen || showDelete || showStartPlaybook || showChangePhase;


  /**
   * Renderiza ações mobile em DropdownMenu compacto
   * Itens ocultos via permissão granular não aparecem no menu.
   */
  const renderMobileActions = () => (
    <div className="flex items-center gap-0.5 shrink-0">
      {hasAnyAction && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {/* Editar (apenas com status aberto + can_edit) */}
            {showEdit && (
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Editar negócio
              </DropdownMenuItem>
            )}

            {showMarkWonLost && (
              <>
                {showEdit && <DropdownMenuSeparator />}
                {/* v1.8.0: Gate explica a trava do Workflow; label muda conforme o motivo */}
                <WorkflowGuardGate decisao={workflowGuardDecision ?? { resultado: 'ALLOW', mensagem: null, bloqueado: false, avisa: false, carregando: false }} side="left">
                  <DropdownMenuItem
                    onClick={ganhoLiberado ? onMarkWon : undefined}
                    disabled={!ganhoLiberado}
                    className="text-emerald-600"
                  >
                    <Trophy className="h-3.5 w-3.5 mr-2" />
                    <span className="truncate">
                      {ganhoLiberado ? 'Marcar como ganho' : motivoGanhoBloqueado}
                    </span>
                  </DropdownMenuItem>
                </WorkflowGuardGate>
                <DropdownMenuItem onClick={onMarkLost} className="text-rose-600">
                  <XCircle className="h-3.5 w-3.5 mr-2" />
                  Marcar como perdido
                </DropdownMenuItem>
              </>
            )}

            {/* v1.9.0: Alterar fase — disponível inclusive em negócio ganho/perdido */}
            {showChangePhase && (
              <>
                {(showEdit || showMarkWonLost) && <DropdownMenuSeparator />}
                <DropdownMenuItem onClick={onChangePhase}>
                  <GitBranch className="h-3.5 w-3.5 mr-2" />
                  Alterar fase
                </DropdownMenuItem>
              </>
            )}

            {showReopen && (
              <DropdownMenuItem onClick={onReopen}>
                <RotateCcw className="h-3.5 w-3.5 mr-2" />
                Reabrir negócio
              </DropdownMenuItem>
            )}

            {/* v1.7.0: Iniciar playbook — só aparece quando elegível */}
            {showStartPlaybook && (
              <>
                {(showEdit || showMarkWonLost || showReopen) && <DropdownMenuSeparator />}
                <DropdownMenuItem onClick={onStartPlaybook}>
                  <Zap className="h-3.5 w-3.5 mr-2 text-primary" />
                  {startPlaybookLabel}
                </DropdownMenuItem>
              </>
            )}


            {showDelete && (
              <>
                {(showEdit || showMarkWonLost || showReopen || showStartPlaybook) && <DropdownMenuSeparator />}
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Excluir negócio
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Botão Fechar (sempre visível) */}
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );

  /**
   * Renderiza ações desktop com tooltips individuais
   * Botões ocultos via permissão granular não são renderizados.
   */
  const renderDesktopActions = () => (
    <div className="flex items-center gap-0.5 shrink-0">
      {/* Editar */}
      {showEdit && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", isDealClosed && "opacity-50 cursor-not-allowed")}
              onClick={isDealClosed ? undefined : onEdit}
              disabled={isDealClosed}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">{isDealClosed ? "Reabra o negócio para editar" : "Editar negócio"}</p>
          </TooltipContent>
        </Tooltip>
      )}

      {showMarkWonLost && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
            !ganhoLiberado && "opacity-50 cursor-not-allowed"
                )}
                  onClick={ganhoLiberado ? onMarkWon : undefined}
                  disabled={!ganhoLiberado}
                >
                  <Trophy className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              {/* v1.8.0: tooltip prioriza o motivo da trava do Workflow */}
              <TooltipContent side="bottom">
                <p className="text-xs max-w-[280px]">{ganhoLiberado ? "Marcar como ganho" : motivoGanhoBloqueado}</p>
              </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="icon"
                className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                onClick={onMarkLost}
              >
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p className="text-xs">Marcar como perdido</p></TooltipContent>
          </Tooltip>
        </>
      )}

      {/* v1.9.0: Alterar fase (mesmo ícone/padrão do modal de Tarefas) */}
      {showChangePhase && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onChangePhase}>
              <GitBranch className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">
              {isDealClosed ? 'Alterar fase (mover para outro processo)' : 'Alterar fase'}
            </p>
          </TooltipContent>
        </Tooltip>
      )}

      {showReopen && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onReopen}>
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p className="text-xs">Reabrir negócio</p></TooltipContent>
        </Tooltip>
      )}

      {showDelete && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p className="text-xs">Excluir negócio</p></TooltipContent>
        </Tooltip>
      )}

      {/* v1.7.0: Botão dedicado de iniciar playbook (desktop). Só renderiza
          quando o deal é elegível — caso contrário fica oculto. */}
      {showStartPlaybook && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary hover:bg-primary/10"
              onClick={onStartPlaybook}
            >
              <Zap className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">{playbookTooltip}</p>
          </TooltipContent>
        </Tooltip>
      )}


      {/* v1.10.0: Criar O.S. (desktop e mobile) */}
      {onCreateServiceOrder && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCreateServiceOrder}>
              <Wrench className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p className="text-xs">Criar O.S.</p></TooltipContent>
        </Tooltip>
      )}

      {/* Separador apenas quando há ações antes do botão Fechar */}
      {hasAnyAction && <div className="h-4 w-px bg-border mx-1" />}

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom"><p className="text-xs">Fechar</p></TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 h-12 border-b bg-background shrink-0">
      {/* Status Badge */}
      <DealStatusBadgePremium status={deal.status} size="sm" />

      {/* Código do Negócio - oculto no mobile para economizar espaço */}
      {!isMobile && (
        <Badge 
          variant="outline" 
          className="text-[10px] px-1.5 py-0.5 h-5 font-mono bg-muted/50 text-muted-foreground border-border shrink-0"
          title={`Código do negócio: ${formatDealCode(deal.id)}`}
        >
          {formatDealCode(deal.id)}
        </Badge>
      )}

      {/* Etapa atual da esteira (Workflow 2.0) — só aparece com módulo ligado e instância ativa */}
      {!isMobile && <WorkflowStageChip rootEntityType="crm_deal" rootEntityId={deal.id} />}

      {/* Título truncado */}
      <h2 className="text-xs md:text-sm font-semibold truncate flex-1 min-w-0">
        {deal.title}
      </h2>


      {/* Valor destacado - oculto no mobile (aparece nas métricas da aba Resumo) */}
      {!isMobile && (
        <PriceDisplay 
          amount={deal.value} 
          currency={deal.currency} 
          size="sm"
          className="font-bold text-primary shrink-0"
        />
      )}

      {/* Separador visual - apenas desktop */}
      {!isMobile && <div className="h-4 w-px bg-border shrink-0" />}

      {/* Ações: DropdownMenu no mobile, botões com tooltip no desktop */}
      {isMobile ? renderMobileActions() : renderDesktopActions()}
    </div>
  );
}
